import { DatePicker, Select, Space, Card, Button, message } from "antd";
import { useTranslation } from "react-i18next";
import { useGetLevelCTerms } from "@hooks";
import { LevelCReportFilter, LevelCTerm } from "../../types/levelc-reports";
import dayjs, { Dayjs } from "dayjs";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import { useMemo } from "react";

const { RangePicker } = DatePicker;

interface LevelCReportFiltersProps {
  filter: LevelCReportFilter;
  onFilterChange: (filter: LevelCReportFilter) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
}

export const LevelCReportFilters = ({
  filter,
  onFilterChange,
  onRefresh,
  isLoading = false,
}: LevelCReportFiltersProps) => {
  const { t } = useTranslation();
  // Fetch terms within selected date range so dropdown shows relevant exam periods
  const { data: terms, isLoading: termsLoading } = useGetLevelCTerms(
    filter.startDate,
    filter.endDate
  );

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      // Validate start <= end
      if (dates[0].isAfter(dates[1])) {
        message.warning(t("levelCReports.filters.invalidDateRange"));
        return;
      }
      // Validate range is not more than 2 years
      if (dates[1].diff(dates[0], 'year', true) > 2) {
        message.warning(t("levelCReports.filters.dateRangeTooLarge"));
        return;
      }
      onFilterChange({
        ...filter,
        startDate: dates[0].startOf('day').toISOString(),
        endDate: dates[1].endOf('day').toISOString(),
        examTermId: undefined, // Reset term when dates change
      });
    }
  };

  // Disable future dates
  const disabledDate = (current: Dayjs) => {
    return current && current.isAfter(dayjs().endOf('day'));
  };

  const handleTermChange = (value: string | undefined) => {
    onFilterChange({
      ...filter,
      examTermId: value,
    });
  };

  const dateValue: [Dayjs | null, Dayjs | null] = [
    filter.startDate ? dayjs(filter.startDate) : null,
    filter.endDate ? dayjs(filter.endDate) : null,
  ];

  // Format term options with localized dates
  const termOptions = useMemo(() => {
    return [
      { value: undefined, label: t("levelCReports.filters.allTerms") },
      ...(terms?.map((term: LevelCTerm) => ({
        value: term.id,
        // Format date based on current locale
        label: `${term.examName} - ${dayjs(term.date).format('MMM D, YYYY')}`,
      })) || []),
    ];
  }, [terms, t]);

  return (
    <Card 
      size="small" 
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <FilterOutlined />
          {t("levelCReports.filters.title")}
        </Space>
      }
      extra={
        onRefresh && (
          <Button
            icon={<ReloadOutlined spin={isLoading} />}
            onClick={onRefresh}
            disabled={isLoading}
          >
            {t("levelCReports.filters.refresh")}
          </Button>
        )
      }
    >
      <Space wrap size="middle">
        <Space direction="vertical" size="small">
          <span style={{ fontSize: 12, color: "#666" }}>
            {t("levelCReports.filters.dateRange")}
          </span>
          <RangePicker
            value={dateValue}
            onChange={handleDateRangeChange}
            format="DD.MM.YYYY"
            allowClear={false}
            disabled={isLoading}
            disabledDate={disabledDate}
            style={{ minWidth: 280 }}
          />
        </Space>

        <Space direction="vertical" size="small">
          <span style={{ fontSize: 12, color: "#666" }}>
            {t("levelCReports.filters.term")}
          </span>
          <Select
            value={filter.examTermId}
            onChange={handleTermChange}
            placeholder={t("levelCReports.filters.allTerms")}
            allowClear
            loading={termsLoading}
            disabled={isLoading || termsLoading}
            style={{ minWidth: 300 }}
            options={termOptions}
          />
        </Space>
      </Space>
    </Card>
  );
};

